import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import type { MeResponse } from '../../api';
import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { Amount } from '../../components/Amount';
import { SectionHeader } from '../../components/SectionHeader';
import { formatLots } from '../shared/formatters';

import { RiskSummary } from './RiskSummary';
import { homeLinks } from './links';
import { rowDividerSx, sectionSx } from './styles';

/**
 * Open FXMate positions: the count, the risk at stop loss line (P10 R6–R8) and up to three rows
 * (mockup 1; M1 R14). I10: each row opens E8, where M5 shows the channel message and what FXMate
 * understood.
 */
export function PositionsPreview({
  openPositions,
}: {
  readonly openPositions: MeResponse['openPositions'];
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const titleId = useId();
  return (
    <Box component="section" aria-labelledby={titleId} sx={sectionSx}>
      <SectionHeader
        id={titleId}
        title={t('home.positions.title', { total: openPositions.count })}
        action={
          <Link component={RouterLink} to={homeLinks.positions}>
            {t('home.positions.seeAll')}
          </Link>
        }
      />
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <RiskSummary openPositions={openPositions} />
        {openPositions.preview.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
            {t('home.positions.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {openPositions.preview.map((position) => (
              <ListItem key={position.positionId} disablePadding sx={rowDividerSx}>
                <ListItemButton
                  component={RouterLink}
                  to={homeLinks.positions}
                  sx={{ gap: 1.25, py: 1.5, px: 1.75 }}
                >
                  <Box
                    component="span"
                    sx={(theme) => ({
                      flex: 'none',
                      width: 40,
                      fontSize: 12,
                      fontWeight: 700,
                      color: position.side === 'buy' ? theme.fx.success : theme.fx.danger,
                    })}
                  >
                    {t(`home.positions.${position.side}`)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                      {position.instrument}{' '}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: NUMERIC_FONT,
                          fontWeight: 400,
                          fontSize: 13,
                          color: 'text.secondary',
                        }}
                      >
                        {t('home.positions.lots', {
                          lots: formatLots(position.volumeLots, language),
                        })}
                      </Box>
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 13,
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {position.channelName}
                    </Box>
                  </Box>
                  <Amount {...position.profit} signed />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}
